import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, flushEvents } from '../lib/analytics';
import { SITE_NAME } from '../lib/brand';

const HIDE_POPUP_KEY = 'x1493_hide_popup';
const SESSION_SHOWN_KEY_PREFIX = 'x1493_popup_shown_';
const ATTRIBUTION_KEY = 'x1493_popup_attribution';

interface Popup {
  id: number;
  name: string;
  isActive: boolean;
  priority: number;
  targetPages: string[];
  excludePages: string[];
  targetType: 'combined' | 'include' | 'exclude';
  delaySeconds: number;
  productImage?: string;
  productName?: string;
  eyebrow?: string;
  tagline?: string;
  benefits?: string;
  benefitsRotating?: boolean;
  ctaText?: string;
  ctaHref?: string;
  price?: string;
  maxShowsPerSession: number;
  trackingId?: string;
}

const DEFAULT_BENEFITS = [
  '嚴選優質素材，用心製作',
  '多款人氣產品，總有一款合你心水',
  '即買即寄，輕鬆購物',
];

const DEFAULT_IMAGE = '/logo.png';
const DEFAULT_CTA_HREF = '/store';

export default function ProductRecommendPopup() {
  const location = useLocation();
  const [popup, setPopup] = useState<Popup | null>(null);
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [loading, setLoading] = useState(true);

  // 載入並匹配當前頁面的 popup
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetch(`/api/public/popups?pagePath=${encodeURIComponent(location.pathname)}`)
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        const popups = res?.data?.popups || res?.popups || [];
        if (popups.length > 0) {
          setPopup(popups[0]);
        }
      })
      .catch(() => {
        // 失敗時靜默不顯示，避免破壞體驗
      })
      .finally(() => setLoading(false));

    return () => { cancelled = true; };
  }, [location.pathname]);

  // 延遲彈出；同一 popup 在同一 session 只顯示指定次數
  useEffect(() => {
    if (loading || !popup || !popup.isActive || typeof window === 'undefined') return;

    const alreadyHidden = window.localStorage.getItem(HIDE_POPUP_KEY) === 'true';
    if (alreadyHidden) return;

    const sessionKey = `${SESSION_SHOWN_KEY_PREFIX}${popup.id}`;
    const shownCount = Number(window.sessionStorage.getItem(sessionKey) || '0');
    if (shownCount >= popup.maxShowsPerSession) return;

    const delayMs = Math.max(0, popup.delaySeconds) * 1000;
    const timer = setTimeout(() => {
      setVisible(true);
      window.sessionStorage.setItem(sessionKey, String(shownCount + 1));

      // 追蹤 impression
      trackEvent('popup_impression', {
        popupId: popup.trackingId || String(popup.id),
        popupName: popup.name,
        pagePath: location.pathname,
      });
      flushEvents();
    }, delayMs);

    return () => clearTimeout(timer);
  }, [loading, popup, location.pathname]);

  // 進場動畫
  useEffect(() => {
    if (!visible) return undefined;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  // Esc 關閉 + 鎖背景捲動
  useEffect(() => {
    if (!visible) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose('escape');
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible]);

  const handleClose = (method: 'escape' | 'overlay' | 'button' | 'x') => {
    if (dontShowAgain && typeof window !== 'undefined') {
      window.localStorage.setItem(HIDE_POPUP_KEY, 'true');
    }

    if (popup) {
      trackEvent('popup_close', {
        popupId: popup.trackingId || String(popup.id),
        popupName: popup.name,
        pagePath: location.pathname,
        method,
      });
      flushEvents();
    }

    setEntered(false);
    setTimeout(() => setVisible(false), 300);
  };

  const handleCtaClick = () => {
    if (!popup) return;

    // 記錄 popup 歸因到 sessionStorage，供 order_created 使用
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({
        popupId: popup.trackingId || String(popup.id),
        popupName: popup.name,
        clickedAt: Date.now(),
      }));
    }

    trackEvent('popup_click', {
      popupId: popup.trackingId || String(popup.id),
      popupName: popup.name,
      ctaHref: popup.ctaHref || DEFAULT_CTA_HREF,
      pagePath: location.pathname,
    });
    flushEvents();
  };

  if (loading || !visible || !popup) return null;

  const productImage = popup.productImage || DEFAULT_IMAGE;
  const ctaHref = popup.ctaHref || DEFAULT_CTA_HREF;
  const ctaText = popup.ctaText || '立即選購 →';
  const eyebrow = popup.eyebrow || '為你推薦';
  const tagline = popup.tagline || SITE_NAME;
  const productName = popup.productName || '';
  const benefits = popup.benefits
    ? popup.benefits.split('\n').map((b) => b.trim()).filter(Boolean)
    : DEFAULT_BENEFITS;
  // 賣點輪播：賣點 > 4 行且後台開啟 toggle → 連續向上滾動（每次約見 3 行）
  const rotating = !!popup.benefitsRotating && benefits.length > 4;
  const scrollDuration = Math.max(benefits.length * 3.5, 14);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="x1493-popup-title"
      className="fixed inset-0 z-[999] flex items-center justify-center p-4"
    >
      {/* 背景遮罩 */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => handleClose('overlay')}
      />

      {/* 彈窗本體 */}
      <div
        className={`relative w-full max-w-[420px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out border border-gray-200 dark:border-gray-700 ${
          entered
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-4 scale-95'
        }`}
      >
        <button
          onClick={() => handleClose('x')}
          aria-label="關閉"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/20 hover:bg-black/40 text-white flex items-center justify-center text-lg leading-none transition"
        >
          ×
        </button>

        {/* 產品圖片區 */}
        <div className="relative bg-gray-100 dark:bg-gray-900 aspect-[4/3] flex items-center justify-center overflow-hidden">
          <span className="absolute top-3 left-3 bg-x1493-primary text-white text-[11px] font-bold tracking-wide px-3 py-1 rounded-full">
            {eyebrow}
          </span>
          <img
            src={productImage}
            alt={productName}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="p-6 text-center">
          <h3
            id="x1493-popup-title"
            className="text-xl font-bold text-gray-900 dark:text-white mb-1.5 leading-snug"
          >
            {tagline}
          </h3>
          {productName ? (
            <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-4">{productName}</p>
          ) : null}

          {rotating ? (
            <>
              <style>{`
                @keyframes popup-benefits-scroll { from { transform: translateY(0); } to { transform: translateY(-50%); } }
                .popup-benefits-track { animation: popup-benefits-scroll linear infinite; will-change: transform; }
                .popup-benefits-marquee:hover .popup-benefits-track { animation-play-state: paused; }
                @media (prefers-reduced-motion: reduce) { .popup-benefits-track { animation: none; } }
              `}</style>
              <div className="popup-benefits-marquee h-[82px] overflow-hidden mb-5">
                <div
                  className="popup-benefits-track"
                  style={{ animationDuration: `${scrollDuration}s` }}
                >
                  {[0, 1].map((copy) => (
                    <ul
                      key={copy}
                      aria-hidden={copy === 1}
                      className="text-left space-y-2 mb-2"
                    >
                      {benefits.map((b) => (
                        <li
                          key={`${copy}-${b}`}
                          className="flex items-start gap-2 text-[13.5px] text-gray-800 dark:text-gray-200 leading-relaxed"
                        >
                          <span className="text-x1493-primary font-bold mt-[1px]">✓</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <ul className="text-left space-y-2 mb-5">
              {benefits.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-2 text-[13.5px] text-gray-800 dark:text-gray-200 leading-relaxed"
                >
                  <span className="text-x1493-primary font-bold mt-[1px]">✓</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {popup.price ? (
            <p className="text-x1493-primary font-bold text-lg mb-4">{popup.price}</p>
          ) : null}

          <a
            href={ctaHref}
            onClick={handleCtaClick}
            className="block w-full bg-x1493-primary hover:bg-red-700 text-white font-bold text-sm rounded-full py-3 transition mb-3"
          >
            {ctaText}
          </a>

          <button
            onClick={() => handleClose('button')}
            className="block mx-auto text-[12.5px] text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white underline underline-offset-2 mb-4 transition"
          >
            下次再說
          </button>

          <label className="flex items-center justify-center gap-2 text-[11.5px] text-gray-500 dark:text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-3.5 h-3.5 accent-x1493-primary"
            />
            不再提醒我
          </label>
        </div>
      </div>
    </div>
  );
}
